import { Fetcher } from '@/api/fetcher'
import { API_URL } from '@/constants'
import { Article } from '@/types/Article'
import { ArticleAuthor } from '@/lib/article-authors'
import { PodcastGroup } from '@/types/Podcast'
import { ArticleSort, ArticleTemplate } from '@/types/api/article'
import {
  ArticleCategory,
  ArticleSubcategory,
} from '@/types/api/article-category'
import { PaginationResponse } from '@/types/api/pagination-response'
import { RequestInit } from 'next/dist/server/web/spec-extension/request'
import { ApiHandlerResult } from '..'
import { BaseApi } from '../base'
import { toBooleanModeTerm } from '@/utils/search/boolean-mode-term'
import type {
  ArticleRedirectPlan,
  ArticleRedirectRemoval,
  ArticleRedirectStatusCode,
} from '@/utils/redirects/article-redirect'
import type { RedirectHop } from '@/utils/redirects/slug-redirects'

type GetArticlesOptionsType = {
  page?: number
  pageSize?: number
  tag?: (string | number)[] | string | number
  sort?: ArticleSort
  trending?: boolean
  search?: string
  type?: ArticleTemplate
  title?: string
  category?: string | string[]
  subcategory?: string | string[]
  excludedIds?: number[]
}

export class ArticleApi extends BaseApi {
  constructor(fetcher: Fetcher) {
    super(fetcher)
  }

  async getCategories(
    options?: RequestInit
  ): ApiHandlerResult<ArticleCategory[]> {
    try {
      const res = await this.fetcher.get(API_URL.ARTICLES.CATEGORIES, {
        ...options,
      })

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async getSubcategories(
    params?: { parentSlug?: string } | null,
    options?: RequestInit
  ): ApiHandlerResult<ArticleSubcategory[]> {
    try {
      const res = await this.fetcher.get(API_URL.ARTICLES.SUBCATEGORIES, {
        ...options,
        params: {
          parent_slug: params?.parentSlug,
        },
      })

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async getById(id: string, options?: RequestInit): ApiHandlerResult<Article> {
    try {
      const res = await this.fetcher.get(
        `${API_URL.ARTICLES.GET_BY_ID}/${id}`,
        {
          ...options,
        }
      )

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async getByPath(
    params: {
      path: string
    },
    options?: RequestInit
  ): ApiHandlerResult<Article> {
    const { path, ...restParams } = params
    try {
      const res = await this.fetcher.get(
        `${API_URL.ARTICLES.PATH}/${encodeURIComponent(path)}`,
        {
          params: restParams,
          ...options,
        }
      )

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  // Same as `SupplierApi.search`: the Express handler drops `title` straight
  // into a boolean-mode `MATCH ... AGAINST`, so an operator character in it
  // fails the query rather than narrowing it.
  async search(
    params?: GetArticlesOptionsType,
    options?: RequestInit
  ): ApiHandlerResult<PaginationResponse<Article>> {
    try {
      const res = await this.fetcher.get(API_URL.ARTICLES.SEARCH, {
        params: params?.title
          ? { ...params, title: toBooleanModeTerm(params.title) }
          : params,
        ...options,
      })

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async getPodcastList(
    options?: RequestInit
  ): ApiHandlerResult<PodcastGroup[]> {
    try {
      const res = await this.fetcher.get(API_URL.ARTICLES.PODCAST, {
        ...options,
      })

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async getArticleAuthors(
    articleId: number,
    options?: RequestInit
  ): ApiHandlerResult<ArticleAuthor[]> {
    try {
      const res = await this.fetcher.get(
        `/article-authors/${articleId}`,
        { ...options }
      )

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async addArticleAuthor(
    articleId: number,
    body: { userId: number },
    options?: RequestInit
  ): ApiHandlerResult<ArticleAuthor[]> {
    try {
      const res = await this.fetcher.post(
        `/article-authors/${articleId}`,
        {
          body: JSON.stringify(body),
          ...options,
        }
      )

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async removeArticleAuthor(
    articleId: number,
    userId: number,
    options?: RequestInit
  ): ApiHandlerResult<ArticleAuthor[]> {
    try {
      const res = await this.fetcher.delete(
        `/article-authors/${articleId}`,
        {
          body: JSON.stringify({ userId }),
          ...options,
        }
      )

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  // Works out what redirecting an article would do (flattened destination,
  // re-pointed URLs, warnings) without changing anything.
  async previewArticleRedirect(
    articleId: number,
    body: { statusCode: ArticleRedirectStatusCode; destination: string },
    options?: RequestInit
  ): ApiHandlerResult<{ plan: ArticleRedirectPlan }> {
    try {
      const res = await this.fetcher.post(`/articles/${articleId}/redirect`, {
        body: JSON.stringify({ ...body, preview: true }),
        ...options,
      })

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async applyArticleRedirect(
    articleId: number,
    body: {
      statusCode: ArticleRedirectStatusCode
      destination: string
      confirmWarnings: boolean
    },
    options?: RequestInit
  ): ApiHandlerResult<{
    plan: ArticleRedirectPlan
    redirect: RedirectHop | null
  }> {
    try {
      const res = await this.fetcher.post(`/articles/${articleId}/redirect`, {
        body: JSON.stringify(body),
        ...options,
      })

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async removeArticleRedirect(
    articleId: number,
    options?: RequestInit
  ): ApiHandlerResult<{ removal: ArticleRedirectRemoval }> {
    try {
      const res = await this.fetcher.delete(
        `/articles/${articleId}/redirect`,
        options
      )

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }
}
